import { ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from '@/lib/auth';
import { AppLayout } from '@/components/layout/AppLayout';
import { LoginPage } from '@/pages/LoginPage';
import { LoginCallback } from '@/pages/LoginCallback';
import { MyWorkPage } from '@/pages/MyWorkPage';
import { BoardPage } from '@/pages/BoardPage';
import { EquipoPage } from '@/pages/EquipoPage';
import { ReportesBookingsPage } from '@/pages/ReportesBookingsPage';
import { CxcClientesPage, CxcClientePage } from '@/pages/CxcPages';
import { ReporteOperativosPage } from '@/pages/ReporteOperativosPage';
import { ReportesOperacionesPage } from '@/pages/ReportesOperacionesPage';
import { OperacionesExcelPage } from '@/pages/OperacionesExcelPage';
import { CierresPendientesPage } from '@/pages/CierresPendientesPage';
import { ComunicacionesCxcPage } from '@/pages/ComunicacionesCxcPage';
import {
  AdminLayout,
  AdminIndexRedirect,
  DelegacionesSection,
  EquiposSection,
  SyncSection,
  MonitoringSection,
  NotificationsSection,
  PipelineSection,
  AuditSection,
} from '@/pages/AdminPage';
import type { UserRole } from '@/lib/types';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: (failureCount, err: any) => {
        if (err?.status === 401 || err?.status === 403) return false;
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
    },
  },
});

function ProtectedRoute({
  children,
  roles,
}: {
  children: ReactNode;
  roles?: UserRole[];
}) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-ink-400">
        Cargando…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.rol)) return <Navigate to="/" replace />;
  return <>{children}</>;
}

/**
 * Landing de la ruta index. El supervisor no tiene "Mi trabajo" (es solo
 * lectura), así que se le manda directo a su reporte.
 */
function HomeIndex() {
  const { user } = useAuth();
  if (user?.rol === 'supervisor') return <Navigate to="/reportes/bookings" replace />;
  // director: sin "Mi trabajo" propio — aterriza en el board (ve todo)
  if (user?.rol === 'director') return <Navigate to="/board" replace />;
  // ventas tampoco tiene "Mi trabajo": su vista principal es el board (su cartera).
  if (user?.rol === 'ventas') return <Navigate to="/board" replace />;
  // cobranza vive en el panel de Clientes (CxC).
  if (user?.rol === 'cobranza') return <Navigate to="/clientes" replace />;
  return <MyWorkPage />;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <Toaster position="top-right" richColors />
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/login/callback" element={<LoginCallback />} />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <AppLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<HomeIndex />} />
              {/* cobranza no tiene board: su home es /clientes (el redirect de "/") */}
              <Route
                path="board"
                element={
                  <ProtectedRoute roles={['agente', 'gerente', 'admin', 'supervisor', 'ventas', 'director']}>
                    <BoardPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="board/:agenteId"
                element={
                  <ProtectedRoute roles={['gerente', 'admin', 'supervisor', 'director']}>
                    <BoardPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="comunicaciones-cxc"
                element={
                  <ProtectedRoute roles={['cobranza', 'admin', 'supervisor', 'director']}>
                    <ComunicacionesCxcPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="clientes"
                element={
                  <ProtectedRoute roles={['cobranza', 'admin', 'supervisor', 'gerente', 'director']}>
                    <CxcClientesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="clientes/:norm"
                element={
                  <ProtectedRoute roles={['cobranza', 'admin', 'supervisor', 'gerente', 'director']}>
                    <CxcClientePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="reportes/bookings"
                element={
                  <ProtectedRoute roles={['supervisor', 'admin', 'ventas', 'director']}>
                    <ReportesBookingsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="reportes/operaciones"
                element={
                  <ProtectedRoute roles={['supervisor', 'admin', 'ventas', 'agente', 'gerente', 'director']}>
                    <ReportesOperacionesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="reportes/operaciones-excel"
                element={
                  <ProtectedRoute roles={['supervisor', 'admin', 'agente', 'gerente', 'director']}>
                    <OperacionesExcelPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="reportes/cierres-pendientes"
                element={
                  <ProtectedRoute roles={['admin', 'director']}>
                    <CierresPendientesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="reportes/operativos"
                element={
                  <ProtectedRoute roles={['supervisor', 'admin', 'director']}>
                    <ReporteOperativosPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="equipo"
                element={
                  <ProtectedRoute roles={['gerente', 'admin', 'supervisor', 'director']}>
                    <EquipoPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin"
                element={
                  // gerente entra SOLO por Delegaciones (las demás secciones
                  // están guardadas individualmente como admin)
                  <ProtectedRoute roles={['admin', 'gerente']}>
                    <AdminLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<AdminIndexRedirect />} />
                <Route
                  path="delegaciones"
                  element={
                    <ProtectedRoute roles={['admin', 'gerente']}>
                      <DelegacionesSection />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="equipos"
                  element={
                    <ProtectedRoute roles={['admin']}>
                      <EquiposSection />
                    </ProtectedRoute>
                  }
                />
                <Route path="sync" element={<ProtectedRoute roles={['admin']}><SyncSection /></ProtectedRoute>} />
                <Route path="monitoreo" element={<ProtectedRoute roles={['admin']}><MonitoringSection /></ProtectedRoute>} />
                <Route path="notificaciones" element={<ProtectedRoute roles={['admin']}><NotificationsSection /></ProtectedRoute>} />
                <Route path="pipeline" element={<ProtectedRoute roles={['admin']}><PipelineSection /></ProtectedRoute>} />
                <Route path="auditoria" element={<ProtectedRoute roles={['admin']}><AuditSection /></ProtectedRoute>} />
              </Route>
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
